import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { Camera, X, Loader2, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

const STATUSES = [
    { value: "approved", label: "Approved" },
    { value: "vip", label: "VIP" },
    { value: "pending", label: "Pending" },
    { value: "rejected", label: "Rejected" },
    { value: "blacklist", label: "Blacklist" },
];

export default function AddMemberModal({ open, onClose, onCreated }) {
    const fileRef = useRef(null);
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [phone, setPhone] = useState("");
    const [instagram, setInstagram] = useState("");
    const [status, setStatus] = useState("approved");
    const [photo, setPhoto] = useState(null);
    const [preview, setPreview] = useState("");
    const [busy, setBusy] = useState(false);

    const reset = () => {
        setFirstName(""); setLastName(""); setPhone(""); setInstagram("");
        setStatus("approved"); setPhoto(null); setPreview("");
        if (fileRef.current) fileRef.current.value = "";
    };

    const pickFile = (f) => {
        if (!f) return;
        if (!f.type.startsWith("image/")) { toast.error("Image only."); return; }
        if (f.size > 6 * 1024 * 1024) { toast.error("Max 6 MB."); return; }
        setPhoto(f); setPreview(URL.createObjectURL(f));
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!firstName.trim() || !phone.trim() || !photo) {
            toast.error("First name, phone and a photo are required.");
            return;
        }
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("first_name", firstName.trim());
            fd.append("last_name", lastName.trim());
            fd.append("phone", phone.trim());
            fd.append("instagram", instagram.trim());
            fd.append("status", status);
            fd.append("vip", status === "vip" ? "true" : "false");
            fd.append("photo", photo);
            const { data } = await api.post("/admin/applications", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success(data?.overridden ? "Existing member overridden." : "Member added.");
            reset();
            onCreated?.();
            onClose?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Create failed.");
        } finally {
            setBusy(false);
        }
    };

    if (!open) return null;

    return (
        <div
            className="fixed inset-0 z-[80] flex items-center justify-center bg-black/75 backdrop-blur-sm px-4"
            onClick={() => onClose?.()}
        >
            <motion.form
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                onSubmit={submit}
                onClick={(e) => e.stopPropagation()}
                className="w-full max-w-md p-7 rounded-sm glass-strong gold-border-anim max-h-[92dvh] overflow-y-auto"
                data-testid="add-member-modal"
            >
                <div className="flex items-center justify-between mb-5">
                    <h3 className="font-serif text-2xl inline-flex items-center gap-2">
                        <UserPlus size={18} className="text-gold" /> Add member
                    </h3>
                    <button type="button" onClick={onClose} className="text-ink-secondary hover:text-gold">
                        <X size={18} />
                    </button>
                </div>

                <input
                    ref={fileRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    data-testid="add-photo-input"
                    onChange={(e) => pickFile(e.target.files?.[0])}
                />
                {!preview ? (
                    <button
                        type="button"
                        onClick={() => fileRef.current?.click()}
                        data-testid="add-photo-picker"
                        className="w-full aspect-[4/3] rounded-sm border border-dashed border-gold/50 hover:border-gold/80 flex flex-col items-center justify-center gap-3 bg-bg-secondary/40"
                    >
                        <Camera size={26} className="text-gold" strokeWidth={1.2} />
                        <span className="text-ink-secondary text-sm">Upload member photo</span>
                    </button>
                ) : (
                    <div className="relative aspect-[4/3] rounded-sm overflow-hidden border border-gold/40">
                        <img src={preview} alt="" className="w-full h-full object-cover" />
                        <button
                            type="button"
                            onClick={() => { setPhoto(null); setPreview(""); if (fileRef.current) fileRef.current.value = ""; }}
                            className="absolute top-3 right-3 w-9 h-9 rounded-full glass-strong flex items-center justify-center text-ink-primary hover:text-gold"
                        >
                            <X size={16} />
                        </button>
                    </div>
                )}

                <div className="mt-5 grid grid-cols-2 gap-4">
                    <div>
                        <label className="label-eyebrow block mb-2">First name</label>
                        <input data-testid="add-first" className="input-line" value={firstName} onChange={(e) => setFirstName(e.target.value)} maxLength={40} autoFocus />
                    </div>
                    <div>
                        <label className="label-eyebrow block mb-2">Last name</label>
                        <input data-testid="add-last" className="input-line" value={lastName} onChange={(e) => setLastName(e.target.value)} maxLength={40} />
                    </div>
                </div>

                <div className="mt-4">
                    <label className="label-eyebrow block mb-2">Phone</label>
                    <input data-testid="add-phone" className="input-line" value={phone} onChange={(e) => setPhone(e.target.value)} inputMode="tel" />
                </div>

                <div className="mt-4">
                    <label className="label-eyebrow block mb-2">Instagram (optional)</label>
                    <input data-testid="add-ig" className="input-line" value={instagram} onChange={(e) => setInstagram(e.target.value)} maxLength={50} />
                </div>

                <div className="mt-5">
                    <label className="label-eyebrow block mb-2">Initial status</label>
                    <div className="flex flex-wrap gap-1.5">
                        {STATUSES.map((s) => (
                            <button
                                key={s.value}
                                type="button"
                                onClick={() => setStatus(s.value)}
                                data-testid={`add-status-${s.value}`}
                                className={`px-3 py-1.5 text-[10px] tracking-[0.22em] uppercase rounded-sm border transition-colors ${
                                    status === s.value
                                        ? "bg-gold text-black border-gold"
                                        : "border-emerald-line/60 text-ink-secondary hover:text-gold hover:border-gold/40"
                                }`}
                            >{s.label}</button>
                        ))}
                    </div>
                    <p className="mt-3 text-[11px] text-ink-muted">
                        If a member already exists with this phone, their record will be overridden (lifts a prior rejection or refreshes their data).
                    </p>
                </div>

                <button
                    type="submit"
                    disabled={busy || !firstName.trim() || !phone.trim() || !photo}
                    data-testid="add-member-submit"
                    className="mt-7 w-full btn-gold inline-flex items-center justify-center gap-2 px-6 py-3 rounded-sm font-medium disabled:opacity-40"
                >
                    {busy ? <Loader2 size={16} className="animate-spin" /> : <UserPlus size={16} />}
                    {busy ? "Saving…" : "Add member"}
                </button>
            </motion.form>
        </div>
    );
}
